/* Cart drawer and cart page (sections/sl-cart-drawer.liquid, sections/sl-cart-page.liquid). Track 1; Track 2 inherits
   it. Contract: CONTRACTS.md "Cart drawer" and "Advent bundles in the cart".
   - Both surfaces are custom elements (<sl-cart-drawer>, <sl-cart-page>) whose sections render this script once,
     outside the HTML that changes, so it runs whatever the cart holds.
   - Every change is ONE /cart/update.js call with the lines' keys, asking Shopify to re-render every cart section on
     the page (Section Rendering API); each surface swaps the inside of its [data-sl-cart-body]. Lines of one
     _BundleId change together, and a lone leftover bundle line is removed in the same call (planUpdate).
   - The drawer: a native modal <dialog> with a Tab trap, Escape and backdrop close, focus back to the opener.
     Any #cart-icon-bubble or [data-sl-cart-open] opens it (one capture-phase handler, so Dawn's header and ours
     both work). GemPages' add-to-cart opens it through window.GPF.openCartDrawer.
   - While the Upcart app's embed is on (#upCart in the page), the drawer stands down and Upcart keeps the cart.
   Public: window.SLCart.open(opener), .close(), .refresh({ open, opener }), .renderContents(state, { open, opener }),
   .planUpdate(lines, key, quantity). */
(() => {
  if (window.SLCart) return;

  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])';
  const TRIGGER = '#cart-icon-bubble, [data-sl-cart-open]';
  const surfaces = new Set();
  const upcartOn = () => Boolean(document.getElementById('upCart'));
  const drawer = () => document.querySelector('sl-cart-drawer');
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Which lines change, as { key: quantity } for /cart/update.js. `lines`: [{ key, qty, bundle, role }].
  function planUpdate(lines, key, quantity) {
    const target = lines.find((l) => l.key === key);
    if (!target) return {};
    const updates = { [key]: quantity };
    if (!target.bundle) return updates;
    const group = lines.filter((l) => l.bundle === target.bundle);
    if (target.role !== 'gift') {
      // A component moves the whole bundle with it: removed, or scaled to the new count.
      for (const l of group) updates[l.key] = quantity === 0 ? 0 : Math.max(0, Math.round((l.qty * quantity) / target.qty));
    }
    const left = group.filter((l) => (l.key in updates ? updates[l.key] : l.qty) > 0);
    if (left.length === 1) updates[left[0].key] = 0;
    return updates;
  }

  const readLines = (root) =>
    [...root.querySelectorAll('[data-sl-line]')].map((el) => ({
      key: el.dataset.slLine,
      qty: Number(el.dataset.slQty),
      bundle: el.dataset.slBundle || '',
      role: el.dataset.slRole || '',
    }));

  const sectionIds = () => [...surfaces].filter((s) => s.isConnected).map((s) => s.sectionId);

  function apply(sections) {
    if (!sections) return;
    let bubble = null;
    for (const s of surfaces) {
      const html = sections[s.sectionId];
      if (!html) continue;
      const fresh = new DOMParser().parseFromString(html, 'text/html');
      const body = fresh.querySelector('[data-sl-cart-body]');
      if (body) s.swap(body);
      bubble = bubble || fresh.querySelector('template[data-sl-cart-bubble]');
    }
    if (bubble) {
      for (const icon of document.querySelectorAll('#cart-icon-bubble')) {
        icon.querySelector('.cart-count-bubble')?.remove();
        icon.append(bubble.content.cloneNode(true));
      }
    }
  }

  async function readJson(response) {
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.status) throw new Error(data.description || data.message || response.statusText);
    return data;
  }

  async function send(updates, from) {
    const root = from || drawer() || [...surfaces][0];
    const url = `${root.dataset.updateUrl}.js`;
    surfaces.forEach((s) => s.busy(true));
    try {
      const data = await readJson(
        await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates, sections: sectionIds(), sections_url: window.location.pathname }),
        })
      );
      surfaces.forEach((s) => s.error(''));
      apply(data.sections);
      surfaces.forEach((s) => s.announce());
    } catch (err) {
      (from || root).error(err.message);
    } finally {
      surfaces.forEach((s) => s.busy(false));
    }
  }

  async function refresh({ open = false, opener } = {}) {
    const root = drawer() || [...surfaces][0];
    if (!root) return;
    const data = await readJson(await fetch(`${root.dataset.rootUrl}?sections=${sectionIds().join(',')}`, { headers: { Accept: 'application/json' } }));
    apply(data);
    if (open) drawer()?.open(opener);
  }

  class SLCartSurface extends HTMLElement {
    connectedCallback() {
      surfaces.add(this);
      this.addEventListener('click', this.onAction);
    }

    disconnectedCallback() {
      surfaces.delete(this);
    }

    get sectionId() {
      return this.dataset.section;
    }

    get body() {
      return this.querySelector('[data-sl-cart-body]');
    }

    onAction = (event) => {
      const button = event.target.closest('[data-sl-action]');
      if (!button || !this.contains(button)) return;
      event.preventDefault();
      const lines = readLines(this.body);
      const action = button.dataset.slAction;
      let updates;
      if (action === 'remove-group') {
        const id = button.dataset.slGroupId;
        updates = Object.fromEntries(lines.filter((l) => l.bundle === id).map((l) => [l.key, 0]));
      } else {
        const key = button.closest('[data-sl-line]').dataset.slLine;
        updates = planUpdate(lines, key, action === 'remove' ? 0 : Math.max(0, Number(button.dataset.slTo)));
      }
      this.focusKey = button.closest('[data-sl-line]')?.dataset.slLine;
      send(updates, this);
    };

    swap(freshBody) {
      const hadFocus = this.body.contains(document.activeElement);
      this.body.innerHTML = freshBody.innerHTML;
      if (!hadFocus) return;
      const same = this.focusKey && this.body.querySelector(`[data-sl-line="${CSS.escape(this.focusKey)}"] [data-sl-action]`);
      (same || this.querySelector('.sl-cart__title')).focus();
    }

    busy(on) {
      if (on) this.body.setAttribute('aria-busy', 'true');
      else this.body.removeAttribute('aria-busy');
    }

    error(message) {
      const line = this.querySelector('[data-sl-cart-error]');
      if (line) line.textContent = message;
    }

    // The live region reads the new total (or the empty message): no copy of its own.
    announce() {
      const status = this.querySelector('[data-sl-cart-status]');
      const source = this.body.querySelector('[data-sl-cart-total], [data-sl-cart-empty] p');
      if (status && source) status.textContent = source.textContent.replace(/\s+/g, ' ').trim();
    }
  }

  class SLCartDrawer extends SLCartSurface {
    connectedCallback() {
      super.connectedCallback();
      this.dialog = this.querySelector('dialog');
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });
      this.dialog.addEventListener('keydown', this.onKeydown);
      this.dialog.addEventListener('click', (event) => {
        if (event.target.closest('[data-sl-cart-close]')) return this.close();
        if (event.target !== this.dialog) return;
        const r = this.dialog.getBoundingClientRect();
        const outside = event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom;
        if (outside) this.close();
      });
    }

    open(opener) {
      if (opener) this.opener = opener;
      if (this.dialog.open && !this.dialog.hasAttribute('data-closing')) return;
      this.dialog.removeAttribute('data-closing');
      if (!this.dialog.open) this.dialog.showModal();
      document.documentElement.classList.add('sl-cart-locked');
      this.querySelector('[data-sl-cart-close]').focus();
    }

    close() {
      if (!this.dialog.open || this.dialog.hasAttribute('data-closing')) return;
      const finish = () => {
        if (!this.dialog.hasAttribute('data-closing')) return;
        this.dialog.removeAttribute('data-closing');
        this.dialog.close();
        document.documentElement.classList.remove('sl-cart-locked');
        const back = this.opener?.isConnected ? this.opener : document.querySelector('#cart-icon-bubble');
        back?.focus();
      };
      this.dialog.setAttribute('data-closing', '');
      if (reducedMotion()) return finish();
      this.dialog.addEventListener('transitionend', finish, { once: true });
      setTimeout(finish, 400);
    }

    onKeydown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      // The trap walks its own list of stops, so it never depends on the browser's next stop (WebKit's plain Tab skips
      // buttons and links, and stopped on the drawer host instead: audit W20).
      const stops = [...this.dialog.querySelectorAll(FOCUSABLE)].filter((el) => el.getClientRects().length > 0);
      if (!stops.length) return;
      event.preventDefault();
      const at = stops.indexOf(document.activeElement);
      const step = event.shiftKey ? -1 : 1;
      const next = at === -1 ? (event.shiftKey ? stops.length - 1 : 0) : (at + step + stops.length) % stops.length;
      stops[next].focus();
    };
  }

  class SLCartPage extends SLCartSurface {}

  customElements.define('sl-cart-drawer', SLCartDrawer);
  customElements.define('sl-cart-page', SLCartPage);

  // Every cart icon opens the drawer (capture phase: before Dawn's or any link handler), unless Upcart is on.
  document.addEventListener(
    'click',
    (event) => {
      const trigger = event.target.closest?.(TRIGGER);
      const d = drawer();
      if (!trigger || !d || upcartOn()) return;
      event.preventDefault();
      event.stopPropagation();
      d.open(trigger);
    },
    true
  );

  function wire() {
    if (!drawer() || upcartOn()) return;
    for (const trigger of document.querySelectorAll(TRIGGER)) {
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.setAttribute('aria-controls', 'sl-cart');
    }
    // GemPages' add-to-cart ("open cart drawer") calls window.GPF.openCartDrawer(addResponse) when it exists.
    window.GPF = window.GPF || {};
    window.GPF.openCartDrawer = () => refresh({ open: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', wire);
  else wire();

  window.SLCart = {
    open: (opener) => drawer()?.open(opener),
    close: () => drawer()?.close(),
    refresh,
    // Dawn-style: `state.sections` from a /cart/add.js call that asked for sectionIds(); otherwise fetch them.
    renderContents(state, { open = true, opener } = {}) {
      if (state?.sections && sectionIds().some((id) => state.sections[id])) {
        apply(state.sections);
        if (open) drawer()?.open(opener);
        return Promise.resolve();
      }
      return refresh({ open, opener });
    },
    sectionIds,
    planUpdate,
  };
})();
